import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { hasModule } from "@/lib/access";

export const MODULES = [
  { key: "obligations", label: "Obligations", desc: "Payments, renewals, EMIs and subscriptions" },
  { key: "compliance", label: "Compliance", desc: "Compliance register, Filing board, Invoice register, Calendar setup" },
  { key: "tasks", label: "Tasks", desc: "Tasks, Task overview, Time sheet, Utilisation" },
  { key: "inquiries", label: "Inquiries", desc: "Inquiry register, journey, follow-ups, contacts" },
  { key: "vault", label: "File Vault", desc: "Physical files, document index, storage locations, issue and return" },
];

export const DeptPicker = ({ departments, value, onChange, multi, testid, allowNone = true }) => (
  multi ? (
    <div className="space-y-1.5 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2" data-testid={testid}>
      {departments.length === 0 && <div className="text-xs text-slate-400">No departments available.</div>}
      {departments.map((d) => (
        <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox data-testid={`${testid}-${d.id}`} checked={value.includes(d.id)}
            onCheckedChange={(c) => onChange(c ? [...value, d.id] : value.filter((x) => x !== d.id))} />
          {d.name}
        </label>
      ))}
    </div>
  ) : (
    <Select value={value[0] || "__none"} onValueChange={(v) => onChange(v === "__none" ? [] : [v])}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Pick a department" /></SelectTrigger>
      <SelectContent>
        {allowNone && <SelectItem value="__none">No department</SelectItem>}
        {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
      </SelectContent>
    </Select>
  )
);

const ModuleSwitches = ({ value, onChange, me }) => (
  <div className="space-y-2">
    {MODULES.map((m) => {
      const locked = !hasModule(me, m.key) && !value[m.key];
      return (
        <div key={m.key} className="flex items-center justify-between gap-3 border border-slate-200 rounded-md px-3 py-2">
          <div>
            <div className="text-sm font-medium">{m.label}</div>
            <div className="text-[11px] text-slate-500">{locked ? "You don't have this module, so you can't grant it" : m.desc}</div>
          </div>
          <Switch data-testid={`access-module-${m.key}`} checked={!!value[m.key]} disabled={locked}
            onCheckedChange={(v) => onChange({ ...value, [m.key]: v })} />
        </div>
      );
    })}
  </div>
);

export const UserAccessDialog = ({ target, me, departments, users, onClose, onSaved }) => {
  const [depts, setDepts] = useState([]);
  const [mods, setMods] = useState({});
  const [mgr, setMgr] = useState("__none");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!target) return;
    setDepts(target.department_ids || []);
    setMods(target.modules || {});
    setMgr(target.manager_id || "__none");
  }, [target]);
  if (!target) return null;
  const isAdmin = me?.role === "admin";
  const pool = isAdmin ? departments : departments.filter((d) => (me?.department_ids || []).includes(d.id));
  const heads = users.filter((u) => u.role === "top_management" && u.id !== target.id);
  const save = async () => {
    setBusy(true);
    try {
      const body = { department_ids: depts, modules: mods };
      if (isAdmin && target.role === "top_management") {
        if (mgr === "__none") body.clear_manager = true; else body.manager_id = mgr;
      }
      await api.patch(`/admin/users/${target.id}/access`, body);
      toast.success("Access updated");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="user-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-blue-700" /> Access for {target.name || target.email}</DialogTitle>
          <DialogDescription>Departments decide who manages this person. Modules decide what they can open.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{target.role === "employee" ? "Department" : "Departments"}</Label>
            <div className="mt-1"><DeptPicker departments={pool} value={depts} onChange={setDepts} multi={target.role !== "employee"} testid="access-departments" allowNone={isAdmin} /></div>
          </div>
          {isAdmin && target.role === "top_management" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reports to (Head)</Label>
              <Select value={mgr} onValueChange={setMgr}>
                <SelectTrigger data-testid="access-reports-to" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Administrator (no Head)</SelectItem>
                  {heads.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Modules</Label>
            <div className="mt-1"><ModuleSwitches value={mods} onChange={setMods} me={me} /></div>
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="access-save" onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800 text-white">{busy ? "Saving…" : "Save access"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
